import { useState } from "react";
import { ClipboardList } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/lib/i18n";

const EVIDENCE_STYLE = {
  "Organizer Reviewed": "bg-emerald-100 text-emerald-800",
  "Attendance Recorded via PIN/QR": "bg-teal-100 text-teal-800",
  "Evidence Attached": "bg-sky-100 text-sky-800",
  "Self-Reported": "bg-amber-100 text-amber-800",
};
const ALL = "__all__";

export default function EvidenceMatrix({ records }) {
  const { t } = useLang();
  const [cat, setCat] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const cats = [...new Set(records.map((r) => r.category))];
  const statuses = [...new Set(records.map((r) => r.status))];
  const filtered = records.filter((r) => (cat === ALL || r.category === cat) && (status === ALL || r.status === status));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6" data-testid="evidence-matrix">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading font-extrabold text-slate-900 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-emerald-600" /> {t("mx_title")}
        </h3>
        <div className="flex gap-2">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="matrix-filter-category" className="rounded-xl w-40 h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>{t("mx_all_cat")}</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="matrix-filter-status" className="rounded-xl w-32 h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>{t("mx_all_status")}</SelectItem>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
        <div className="grid grid-cols-12 bg-slate-100 text-[10px] font-black text-slate-600 uppercase px-4 py-2 min-w-[700px]">
          <span className="col-span-3">{t("mx_activity")}</span><span className="col-span-3">{t("mx_output")}</span><span className="col-span-3">{t("mx_outcome")}</span><span className="col-span-1">{t("mx_status")}</span><span className="col-span-2 text-right">{t("mx_evidence")}</span>
        </div>
        {filtered.map((r, i) => (
          <div key={i} data-testid={`matrix-row-${i}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-slate-100 text-xs items-center min-w-[700px]">
            <span className="col-span-3 font-bold text-slate-800">{r.activity}</span>
            <span className="col-span-3 text-slate-600">{r.output}</span>
            <span className="col-span-3 text-emerald-700 font-semibold">{r.outcome}</span>
            <span className="col-span-1 text-[10px] font-bold text-slate-500">{r.status}</span>
            <span className={`col-span-2 text-[9px] font-black px-2 py-1 rounded-full text-center ${EVIDENCE_STYLE[r.evidence] || "bg-slate-100"}`}>{r.evidence}</span>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-xs text-slate-400 py-6">{t("mx_empty")}</p>}
      </div>
    </div>
  );
}
